import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import { postDataAPI } from '../../utils/fetchData';
import { getTeamMembersForTasks } from '../../utils/approvalUtils';
import CustomCalendar from '../../components/Calendar/CustomCalendar';

const QuickReminder = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userArticle } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTeam, setLoadingTeam] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        due_date: '',
        assignee: '',
        priority: 'medium',
        auto_reminder: 'on_due_date' // none, 1_day_before, on_due_date
    });

    // Follow-up reminders state
    const [followUpReminders, setFollowUpReminders] = useState([
        { days: '30', time: '8am' },  // Follow Up 1 - furthest out
        { days: '14', time: '8am' },  // Follow Up 2 - middle
        { days: '1', time: '8am' }    // Follow Up 3 - closest
    ]);

    // Generate days options: Day of + 1-60 days
    const daysOptions = ['day_of', ...Array.from({ length: 60 }, (_, i) => (i + 1).toString())];
    
    // Generate time options: 12am-12pm
    const timeOptions = [
        '12am', '1am', '2am', '3am', '4am', '5am', 
        '6am', '7am', '8am', '9am', '10am', '11am', '12pm'
    ];

    // Fetch team members on component mount
    useEffect(() => {
        fetchTeamMembers();
    }, []);

    const fetchTeamMembers = async () => {
        setLoadingTeam(true);
        try {
            // Use the same logic as approval system for consistency
            const members = await getTeamMembersForTasks();
            
            // Convert to the format expected by this component
            const formattedMembers = members.map(member => ({
                id: member._id,
                name: member.name,
                role: member.role,
                email: member.email,
                _id: member._id
            }));
            
            setTeamMembers(formattedMembers);
        } catch (error) {
            console.error('Error fetching team members:', error);
            // Fallback to just current user
            const currentUser = {
                id: userArticle?._id || 'self',
                name: `${userArticle?.username || userArticle?.email || 'Me'} (me)`,
                role: userArticle?.professionRole || 'Self',
                email: userArticle?.email,
                _id: userArticle?._id
            };
            setTeamMembers([currentUser]);
        } finally {
            setLoadingTeam(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFollowUpChange = (index, field, value) => {
        setFollowUpReminders(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            // Validation
            if (!formData.title.trim()) {
                dispatch(setErrorAlert('Please enter a title'));
                setIsLoading(false);
                return;
            }

            if (!formData.due_date) {
                dispatch(setErrorAlert('Please select a due date'));
                setIsLoading(false);
                return;
            }

            if (!formData.assignee) {
                dispatch(setErrorAlert('Please select a team member'));
                setIsLoading(false);
                return;
            }

            // Create the task data
            const selectedMember = teamMembers.find(member => member.id === formData.assignee);
            const taskData = {
                title: formData.title,
                description: formData.description || `Task assigned to ${selectedMember?.name || selectedMember?.username}`,
                dueDate: formData.due_date,
                priority: formData.priority,
                type: 'reminder',
                assigneeId: selectedMember?.id || selectedMember?._id, // Send the assignee's ID
                metadata: {
                    assignee_name: selectedMember?.name || selectedMember?.username,
                    assignee_role: selectedMember?.role || selectedMember?.professionRole,
                    assignee_email: selectedMember?.email,
                    auto_reminder: formData.auto_reminder,
                    follow_up_reminders: followUpReminders,
                    quick_reminder: true,
                    created_from: 'tasks_app'
                }
            };

            console.log('🔍 QuickReminder - Task data being sent:', taskData);
            console.log('🔍 QuickReminder - Selected member:', selectedMember);
            console.log('🔍 QuickReminder - Assignee ID being sent:', selectedMember?.id || selectedMember?._id);
            console.log('🔍 QuickReminder - Current user ID:', userArticle?._id);

            // Create task via API
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const response = await postDataAPI('user/tasks', taskData, token);
            
            console.log('🔍 QuickReminder - API response:', response);
            
            if (response?.data?.success || response?.success) {
                dispatch(setSuccessAlert('Task created successfully'));
                navigate('/tasks');
            } else {
                throw new Error(response?.data?.message || response?.message || 'Failed to create task');
            }

        } catch (error) {
            console.error('Error creating task:', error);
            dispatch(setErrorAlert('Failed to create task'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/tasks');
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>New Task</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/tasks')}
                            >
                                View All
                            </span>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate(-1)}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Create a new task</span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">
                                    Create and assign tasks to team members with deadlines and priorities. 
                                    Perfect for project management and task tracking.
                                </p>
                            </div>

                            {/* Form Fields */}
                            <div className="space-y-4 mb-6">
                                
                                {/* Title Field */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF6B35] hover:bg-[rgba(255,107,53,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Title
                                        <span className="text-red-400 ml-1">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        placeholder="Enter a title..."
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                        required
                                    />
                                </div>

                                {/* Description Field */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF6B35] hover:bg-[rgba(255,107,53,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        placeholder="Enter description (optional)..."
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none"
                                        rows="3"
                                    />
                                </div>

                                {/* Due Date Field */}
                                <CustomCalendar
                                    value={formData.due_date}
                                    onChange={(value) => handleInputChange('due_date', value)}
                                    showTime={true}
                                    label="Due Date *"
                                    placeholder="Select due date and time..."
                                    theme="tasks"
                                    required
                                />

                                {/* Priority Field */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF6B35] hover:bg-[rgba(255,107,53,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Priority
                                    </label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => handleInputChange('priority', e.target.value)}
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px]"
                                    >
                                        <option value="low" className="bg-gray-800">Low</option>
                                        <option value="medium" className="bg-gray-800">Medium</option>
                                        <option value="high" className="bg-gray-800">High</option>
                                    </select>
                                </div>

                                {/* Team Member Assignment */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF6B35] hover:bg-[rgba(255,107,53,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Assign to Team Member
                                        <span className="text-red-400 ml-1">*</span>
                                    </label>
                                    {loadingTeam ? (
                                        <div className="text-[rgba(255,255,255,0.5)] text-[12px]">Loading team members...</div>
                                    ) : (
                                        <select
                                            value={formData.assignee}
                                            onChange={(e) => handleInputChange('assignee', e.target.value)}
                                            className="w-full bg-transparent border-none outline-none text-white text-[12px]"
                                            required
                                        >
                                            <option value="" className="bg-gray-800">Select team member...</option>
                                            {teamMembers.map((member) => (
                                                <option key={member.id} value={member.id} className="bg-gray-800">
                                                    {member.name || member.username} ({member.role || member.professionRole})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                {/* Auto Reminder Settings */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF6B35] hover:bg-[rgba(255,107,53,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Auto Reminder
                                    </label>
                                    <select
                                        value={formData.auto_reminder}
                                        onChange={(e) => handleInputChange('auto_reminder', e.target.value)}
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px]"
                                    >
                                        <option value="none" className="bg-gray-800">No auto reminder</option>
                                        <option value="1_day_before" className="bg-gray-800">1 day before</option>
                                        <option value="on_due_date" className="bg-gray-800">On due date</option>
                                    </select>
                                </div>

                                {/* Follow-up Reminders */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                    <label className="block text-white text-[12px] font-semibold mb-3">
                                        Follow-up Reminders
                                    </label>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mb-3">
                                        Set up automatic follow-up reminders after the due date
                                    </p>
                                    
                                    {followUpReminders.map((reminder, index) => (
                                        <div key={index} className="flex items-center gap-3 mb-3">
                                            <span className="text-white text-[11px] w-16">
                                                Follow Up {index + 1}:
                                            </span>
                                            <select
                                                value={reminder.days}
                                                onChange={(e) => handleFollowUpChange(index, 'days', e.target.value)}
                                                className="flex-1 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded px-2 py-1 text-white text-[11px]"
                                            >
                                                {daysOptions.map(day => (
                                                    <option key={day} value={day} className="bg-gray-800">
                                                        {day === 'day_of' ? 'Day of' : `${day} days after`}
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                value={reminder.time}
                                                onChange={(e) => handleFollowUpChange(index, 'time', e.target.value)}
                                                className="w-20 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded px-2 py-1 text-white text-[11px]"
                                            >
                                                {timeOptions.map(time => (
                                                    <option key={time} value={time} className="bg-gray-800">
                                                        {time}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pb-6">
                                <button
                                    onClick={handleCancel}
                                    className="flex-1 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg py-3 text-white text-[12px] font-medium hover:bg-[rgba(255,255,255,0.15)] transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isLoading || !formData.title || !formData.due_date || !formData.assignee}
                                    className="flex-1 bg-[#FF6B35] rounded-lg py-3 text-white text-[12px] font-medium hover:bg-[#e55a2b] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {isLoading ? 'Creating...' : 'Create Task'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default QuickReminder;
